import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { getDashBoardState } from "@/redux/store";
import { Button, Image, Modal, Spin } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "next-i18next";
import styled, { css } from "styled-components";
import {
  useGetUserInfoQuery,
  usePostG2AccessKeyMutation,
  usePostUserInfoMutation,
} from "@/redux/api/userApi";
import { useSession } from "next-auth/react";
import { useGetEnvListQuery } from "@/redux/api/commonApi";
import { useRouter } from "next/router";
import { G2_LAST_VIEWED_COOKIE_MAX_AGE, G2_LAST_VIEWED_COOKIE_NAME } from "@/constants/g2";
import { useCookies } from "react-cookie";
import { dashboardActions } from "@/redux/stores/dashboardStore";

const Wrapper = styled(Modal)<{ isAgree?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;

  .ant-modal-content {
    border-radius: 15px;
    padding: ${(props) =>
      props.isAgree ? "30px 30px 30px 30px" : "0 0 50px 0"};
  }

  .ant-modal-body {
    position: relative;
    display: flex;
    overflow: hidden;
  }

  ${({ isAgree, theme: { screenSM } }) =>
    !!isAgree &&
    css`
      @media (max-width: ${screenSM}px) {
        &.ant-modal {
          max-width: 100%;
          height: 100%;
          margin: 0;
        }

        &.ant-modal > div {
          height: 100%;
        }

        .ant-modal-content {
          border-radius: 0;
          padding: 30px 0 0 0;
          height: 100%;
        }

        .ant-modal-body {
          overflow-x: auto;
          height: 100%;
        }
      }
    `}
`;

const IframeContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 700px;
  min-width: 380px;
  overflow: hidden;

  ${({ theme: { screenSM } }) =>
    css`
      @media (max-width: ${screenSM}px) {
        min-height: unset;
      }
    `}
`;

const StyledIframe = styled.iframe`
  position: absolute;

  border: none;
  /**
   * G2 페이지 자체에 테두리가 있음\
   * 해당 테두리를 안 보이게 하기 위해서 꼼수 사용함\
   * iframe을 컨테이터보다 상하좌우 2px 씩 더 크게 만들고\
   * -2px 만큼 상좌로 이동시켜서 테두리를 보이지 않게 함
   */
  width: calc(100% + 4px);
  height: calc(100% + 4px);
  top: -2px;
  left: -2px;

  &::-webkit-scrollbar {
    width: 1px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
`;

const StyledInfo = styled.div<{ isMobile?: false }>`
  width: 100%;
  /* height: 0px; */
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;

  .love-text {
    color: #3075f6;
    display: flex;
    margin: 0 auto;
    font-size: 14px;
  }

  .agree-btn {
    display: flex;
    margin: 0 auto;
    justify-content: space-between;
    button {
      border-radius: 30px;
      background-color: #146ef5;
      border: 1px solid #146ef5;
      /* padding: 12px 24px; */
    }
  }

  .hide-btn {
    margin: 20px auto 0 auto;
    color: gray;
    font-size: 14px;
    &:hover {
      color: #3075f6;
      cursor: pointer;
    }
  }

  .logo {
    display: flex;
    justify-content: center;
    margin-top: 30px;
    align-items: center;
    /* width: 90%; */
    img {
      display: flex;
      margin: 0 auto;
      width: 90%;
      height: 200px;
      border-top-right-radius: 6px;
      border-top-left-radius: 6px;
      object-fit: contain;
    }
  }

  .award {
    /* margin-top: 30px; */
    display: flex;
    justify-content: center;
    margin-top: 30px;
    gap: 30px;

    img {
      width: 70px;
      height: 90px;
    }
  }

  .title {
    margin: 10px 10px 0px 10px;
    font-size: ${({ isMobile }) => (isMobile ? "1.5rem" : "24px")};

    font-weight: 700;
    font-family: Inter;
    display: flex;
    justify-content: center;
    color: black;
    text-align: center;
  }

  .info-text {
    margin: 0 auto;

    font-size: ${({ isMobile }) => (isMobile ? "12px" : "16px")};
    font-weight: 500;
    width: 80%;
    font-family: Montserrat;
    display: flex;
    justify-content: center;
    text-align: center;
    flex-direction: column;
    margin-bottom: 20px;
    color: black;

    .text-emphasis {
      font-weight: 600;
    }
  }
`;

const StyledAgreeButton = styled(Button)<{ isMobile?: false }>`
  font-size: 16px;
  font-weight: 600;
  padding: ${({ isMobile }) => (isMobile ? "24px 50px" : "24px 100px")};
  font-family: Poppins;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background-color: black;
  border: 1px solid black;
`;
const StyledLoading = styled(Spin)`
  width: 600px;
  height: 600px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`;

export default function ReviewModal() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const session = useSession();
  const router = useRouter();
  const { t } = useTranslation("common");
  
  const dispatch = useAppDispatch();

  const { accountUserInfo: userInfo, g2ReviewModalOpen } = useAppSelector(
    (store) => getDashBoardState(store)
  );

  const [isShow, setIsShow] = useState<boolean>(false);
  const [isAgree, setIsAgree] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [updateUser] = usePostUserInfoMutation();
  const [isMobile, setIsMobile] = useState(false);

  const isLoggedIn = !!userInfo?.id

  const cookieKey = `${G2_LAST_VIEWED_COOKIE_NAME}|${userInfo?.id}`;

  const [, setCookie] = useCookies([cookieKey]);

  /**
   * G2 리뷰 모달이 표시되면 쿠키를 셋
   */
  useEffect(() => {
    if (!isShow) {
      return;
    }

    if (!isLoggedIn) {
      return;
    }

    setCookie(cookieKey, Date.now(), {
      maxAge: G2_LAST_VIEWED_COOKIE_MAX_AGE,
      path: "/",
    });
  }, [isShow, isLoggedIn, setCookie, cookieKey]);

  /**
   * 페이지가 이동되면 모달 등 상태를 초기화
   */
  useEffect(() => {
    setIsShow(false);
    setIsAgree(false);
    dispatch(dashboardActions.setG2ReviewModalOpen({ isShow: false }));
  }, [router.asPath, dispatch]);

  const [getG2Key] = usePostG2AccessKeyMutation();
  const getAccessKey = useCallback(async () => {
    const response = await getG2Key({});
    if (response.data.success) {
      const url = `https://www.g2.com/partnerships/AIStudios/users/login.embed?state=${response.data.data}&email=${session?.data?.user?.id}`;
      setIframeUrl(url);
    }
  }, [getG2Key, session?.data?.user?.id]);

  const closeModal = () => {
    setIsShow(false);
    setIsAgree(false);
    dispatch(dashboardActions.setG2ReviewModalOpen({ isShow: false }));
  };

  useEffect(() => {
    if (!iframeUrl && session?.data?.user?.id) {
      getAccessKey();
    }
  }, [getAccessKey, iframeUrl, session?.data?.user?.id]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // useEffect(() => {
  //   if (iframeRef.current) {
  //     // window.addEventListener('load', (event)=>{
  //     //   
  //     // })
  //     
  //     

  //     iframeRef.current.addEventListener("onload", (event) => {
  //       setIsReady(true);
  //       
  //     });
  //     // 
  //     // const
  //     // iframeRef.current.style
  //     // const iframeDocument = iframeRef.current;
  //     // 

  //     // document 내부의 텍스트 가져오기
  //     // const textInsideIframe = iframeDocument.body.innerText;
  //     // 
  //   }
  // });

  // const [cookies, setCookie] = useCookies(["isG2reviewed"]);
  useEffect(() => {
    /**
     * 로그인 되어 있지 않으면 모달을 띄우지 않음
     */
    if (!isLoggedIn) {
      return;
    }

    const isG2reviewed = userInfo?.isNewG2reviewed || false;
    const isModalOpen = g2ReviewModalOpen;
    
    /**
     * 기존에는 리뷰 여부를 확인하고 모달을 띄웠으나\
     * 지금은 리뷰 여부를 확인하지 않고 모달을 띄우도록 함
     */
    if (isModalOpen) {
      setIsShow(true);
    }
  }, [isLoggedIn, userInfo, g2ReviewModalOpen]);

  return (
    <Wrapper
      // open={true}
      open={isShow}
      footer={null}
      centered={true}
      onCancel={closeModal}
      width={750}
      title={false}
      isAgree={isAgree}
    >
      {isAgree && iframeUrl && (
        <IframeContainer>
          <StyledIframe
            ref={iframeRef}
            src={iframeUrl}
          />
        </IframeContainer>
      )}
      {/* {isAgree && !isReady && <StyledLoading />} */}
      {!isAgree && (
        <StyledInfo isMobile={isMobile}>
          <div className="logo">
            <Image src="/images/g2_popup_img.png" preview={false} alt="logo" />
          </div>
          <div className="title">{t("Enjoyed your video creation experience?")}</div>
          <div className="info-text">
            <div>
              {t(
                "We hope you loved your video! Please take a moment to leave a review on G2 and share your experience."
              )}
            </div>
          </div>
          <div className="agree-btn">
            <StyledAgreeButton
              isMobile={isMobile}
              onClick={() => {
                setIsAgree(true);
                /**
                 * G2 리뷰 하기 버튼을 누르면 리뷰 한 것으로 취급
                 */
                updateUser({ isNewG2reviewed: true });
              }}
            >
              {t("Leave a Review on G2")}
            </StyledAgreeButton>
          </div>
        </StyledInfo>
      )}
    </Wrapper>
  );
}
